import type { RumGlobal } from '@datadog/browser-rum-slim';

export type RumClient = Pick<RumGlobal, 'addError' | 'addAction' | 'setGlobalContextProperty'>;

type QueuedCall = {
    [Method in keyof RumClient]: [Method, Parameters<RumClient[Method]>];
}[keyof RumClient];

const MAX_QUEUED_CALLS = 100;

let client: RumClient | undefined;
let disabled = false;
let queue: QueuedCall[] = [];

const forward = (rumClient: RumClient, [method, args]: QueuedCall) => {
    (rumClient[method] as (...callArgs: typeof args) => void)(...args);
};

const call = (queuedCall: QueuedCall) => {
    if (client) {
        forward(client, queuedCall);
        return;
    }
    if (!disabled && typeof window !== 'undefined' && queue.length < MAX_QUEUED_CALLS) queue.push(queuedCall);
};

/**
 * Stands in for the Datadog RUM SDK so modules can report errors and actions without importing it.
 * Calls made before the SDK loads are queued and replayed once it is attached, and dropped for
 * sessions that are not sampled, where the SDK is never downloaded.
 */
export const datadogRum: RumClient = {
    addError: (...args) => call(['addError', args]),
    addAction: (...args) => call(['addAction', args]),
    setGlobalContextProperty: (...args) => call(['setGlobalContextProperty', args]),
};

export const attachRumClient = (rumClient: RumClient) => {
    client = rumClient;
    const pending = queue;
    queue = [];
    pending.forEach((queuedCall) => forward(rumClient, queuedCall));
};

export const disableRumClient = () => {
    if (client) return;
    disabled = true;
    queue = [];
};

/** @internal Test-only state isolation for the RUM client facade. */
export const resetRumClientForTesting = () => {
    client = undefined;
    disabled = false;
    queue = [];
};
